/* eslint-disable -- React Compiler rules (react-hooks v6) flagged by external linter are not enabled in this project's CRA toolchain (react-hooks 5.2.0); false positives on event-handler/async setState */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw, Database, CheckCircle, Clock, AlertCircle } from "lucide-react";

const SCRAPERS = [
  { id: "oecsv", name: "ÖCSV (eDart Liga)", description: "Landesliga Oberösterreich - liga.oecsv.at" },
  { id: "oecsv-bl", name: "ÖCSV Bundesliga", description: "Bundesliga Season 70 - liga.oecsv.at" },
  { id: "throwdarts", name: "Throwdarts", description: "Alle Regionen - throwdarts.at" },
];

function formatDate(dateStr) {
  if (!dateStr) return null;
  
  // Handle ISO format (2026-03-27T19:52:14.124791+00:00)
  if (dateStr.includes('T')) {
    const date = new Date(dateStr);
    return date.toLocaleString('de-AT', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  // Handle German format (27.03.2026 20:40)
  return dateStr;
}

export function ScrapeTab({ api }) {
  const [running, setRunning] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get("/scrape/stats");
      setStats(res.data);
    } catch (e) {
      toast.error("Scrape-Statistiken konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const triggerScrape = async (scraperId) => {
    try {
      setRunning(prev => [...prev, scraperId]);
      await api.post(`/scrape/${scraperId}`);
      toast.success(`${SCRAPERS.find(s => s.id === scraperId)?.name} Scrape gestartet`);
      
      // Remove from running after estimated time and reload stats
      const timeout = scraperId === "throwdarts" ? 180000 : 90000;
      setTimeout(() => {
        setRunning(prev => prev.filter(id => id !== scraperId));
        loadStats();
      }, timeout);
    } catch (e) {
      setRunning(prev => prev.filter(id => id !== scraperId));
      if (e.response?.status === 409) {
        toast.error("Scrape läuft bereits");
      } else {
        toast.error(e.response?.data?.detail || "Fehler beim Starten");
      }
    }
  };

  const triggerAll = async () => {
    try {
      setRunning(SCRAPERS.map(s => s.id));
      const res = await api.post("/scrape/all");
      toast.success(`${res.data.started.length} Scrapes gestartet`);
      if (res.data.skipped.length > 0) {
        toast.info(`${res.data.skipped.length} bereits laufend, übersprungen`);
      }
      
      setTimeout(() => {
        setRunning([]);
        loadStats();
      }, 180000);
    } catch (e) {
      setRunning([]);
      toast.error(e.response?.data?.detail || "Fehler beim Starten");
    }
  };

  return (
    <div data-testid="scrape-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>
            Liga-Daten aktualisieren
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manuelle Aktualisierung der Ligaergebnisse von externen Webseiten
          </p>
        </div>
        <button
          onClick={triggerAll}
          disabled={running.length > 0}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold uppercase disabled:opacity-50"
          data-testid="scrape-all-btn"
        >
          <RefreshCw className={`w-4 h-4 ${running.length > 0 ? 'animate-spin' : ''}`} />
          Alle aktualisieren
        </button>
      </div>

      <div className="bg-card border border-white/10 p-4 mb-6">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
          <div>
            <p className="text-sm font-semibold">Automatische Aktualisierung</p>
            <p className="text-xs text-muted-foreground mt-1">
              Die Daten werden automatisch täglich um 03:00 Uhr aktualisiert. 
              Nutze die manuelle Aktualisierung nur bei Bedarf.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SCRAPERS.map(scraper => {
            const isRunning = running.includes(scraper.id);
            const scraperStats = stats[scraper.id] || {};
            const lastUpdate = formatDate(scraperStats.last_update);
            const teamCount = scraperStats.teams || 0;
            
            return (
              <div
                key={scraper.id}
                className={`bg-card border p-5 transition-colors ${isRunning ? 'border-primary/50' : 'border-white/10'}`}
                data-testid={`scrape-card-${scraper.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <Database className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-bold text-sm">{scraper.name}</h3>
                      <p className="text-xs text-muted-foreground mt-1">{scraper.description}</p>
                      {scraper.note && (
                        <p className="text-xs text-yellow-500 mt-1">{scraper.note}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Stats Section */}
                <div className="mt-3 pt-3 border-t border-white/5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Teams:</span>
                    <span className="font-semibold">{teamCount}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs mt-1">
                    <span className="text-muted-foreground">Letztes Update:</span>
                    {lastUpdate ? (
                      <span className="text-green-500 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        {lastUpdate}
                      </span>
                    ) : (
                      <span className="text-yellow-500 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Nie
                      </span>
                    )}
                  </div>
                </div>
                
                <button
                  onClick={() => triggerScrape(scraper.id)}
                  disabled={isRunning}
                  className={`mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 text-xs font-bold uppercase border transition-colors ${
                    isRunning 
                      ? 'border-primary/50 text-primary bg-primary/10' 
                      : 'border-white/10 text-muted-foreground hover:text-foreground hover:border-white/30'
                  }`}
                  data-testid={`scrape-btn-${scraper.id}`}
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isRunning ? 'animate-spin' : ''}`} />
                  {isRunning ? 'Läuft...' : 'Jetzt aktualisieren'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 p-4 border border-white/5 bg-card/50">
        <p className="text-xs text-muted-foreground">
          <strong>Hinweis:</strong> Die Aktualisierung kann je nach Datenquelle 1-5 Minuten dauern. 
          Throwdarts dauert am längsten, da alle Regionen gescraped werden.
        </p>
      </div>
    </div>
  );
}
